import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';

/**
 * Les bornes de société de l'acompte, de l'avance et du prêt (Yros/Kavero#411).
 *
 * Décision du 18/09/2026 : elles se règlent sur « Éléments de rémunération »,
 * là où le gestionnaire de paie saisit déjà les déductions et les prêts. Les
 * bornes d'une opération se règlent là où l'opération se saisit, pas dans une
 * page de réglages où personne n'ira les chercher.
 *
 * **Un champ vide vaut « pas de borne », jamais zéro.** Il s'affiche vide et
 * part en `null` : zéro se lirait comme l'interdiction de toute opération.
 *
 * L'écran ne vérifie que ce qu'il peut voir — un nombre qui n'en est pas un, un
 * pourcentage au-delà de 100, une durée décimale. Le reste appartient à Odoo,
 * qui garde aussi l'écriture, et ses refus s'affichent mot pour mot.
 *
 * Et une borne de société ne peut qu'abaisser la limite légale : c'est au dépôt
 * que le plus bas des deux s'applique, la page n'a rien à refuser à ce titre.
 */

// Chaque borne, dans l'ordre où elle se lit : les deux plafonds de montant,
// puis les deux bornes du prêt. `entier` refuse une valeur décimale, `max` une
// valeur au-delà de la borne naturelle.
const BORNES = [
  { cle: 'plafond_avance', entier: false, max: null, suffixe: 'F CFA' },
  { cle: 'plafond_acompte_pct', entier: false, max: 100, suffixe: '%' },
  { cle: 'duree_max_pret', entier: true, max: null, suffixe: null },
  { cle: 'prets_simultanes_max', entier: true, max: null, suffixe: null },
];

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

/** Valeur du serveur vers le champ : `null` s'affiche vide, jamais « 0 ». */
function versChamp(valeur) {
  return valeur === null || valeur === undefined ? '' : String(valeur);
}

/**
 * Saisie vers la valeur à envoyer, ou `undefined` si elle est fausse.
 *
 * Vide donne `null` — « pas de borne ». `Number` plutôt que `parseFloat` :
 * « 12abc » doit être refusé ici, pas partir chez Odoo.
 */
function versValeur(saisie, { entier, max }) {
  if (saisie === '' || saisie === null || saisie === undefined) return null;
  const nombre = Number(saisie);
  if (!Number.isFinite(nombre) || nombre <= 0) return undefined;
  if (entier && !Number.isInteger(nombre)) return undefined;
  if (max !== null && nombre > max) return undefined;
  return nombre;
}

function ParametresAvances() {
  const { t } = useTranslation();
  const [valeurs, setValeurs] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        const reponse = await apiService.lireParametresAvances();
        if (!actif) return;
        if (reponse && reponse.ok) {
          const champs = {};
          BORNES.forEach(({ cle }) => {
            champs[cle] = versChamp(reponse.parametres?.[cle]);
          });
          setValeurs(champs);
        } else {
          setErreur(reponse?.message || t('parametresAvances.erreurGenerique'));
        }
      } catch (e) {
        if (actif) setErreur(e?.response?.data?.message || t('parametresAvances.erreurGenerique'));
      } finally {
        if (actif) setChargement(false);
      }
    })();
    return () => {
      actif = false;
    };
  }, [t]);

  const enregistrer = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    const corps = {};
    for (const borne of BORNES) {
      const valeur = versValeur(valeurs[borne.cle], borne);
      if (valeur === undefined) {
        setErreur(t(`parametresAvances.invalide.${borne.cle}`));
        return;
      }
      corps[borne.cle] = valeur;
    }

    setEnvoi(true);
    try {
      const reponse = await apiService.ecrireParametresAvances(corps);
      if (reponse && reponse.ok) {
        const champs = {};
        BORNES.forEach(({ cle }) => {
          champs[cle] = versChamp(reponse.parametres?.[cle]);
        });
        setValeurs(champs);
        setSucces(t('parametresAvances.succes'));
      } else {
        setErreur(reponse?.message || t('parametresAvances.erreurGenerique'));
      }
    } catch (e) {
      setErreur(e?.response?.data?.message || t('parametresAvances.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) return <LoadingBarWithStyles />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">{t('parametresAvances.titre')}</h2>
        <p className="text-sm text-gray-600 mt-1">{t('parametresAvances.explication')}</p>
      </div>

      {erreur && (
        <p
          data-testid="parametres-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {erreur}
        </p>
      )}
      {succes && (
        <p
          data-testid="parametres-succes"
          className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
        >
          {succes}
        </p>
      )}

      {valeurs && (
        <form onSubmit={enregistrer} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BORNES.map(({ cle, entier, max, suffixe }) => (
              <label key={cle} className="block text-sm font-medium">
                {t(`parametresAvances.champs.${cle}`)}
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    inputMode={entier ? 'numeric' : 'decimal'}
                    min="1"
                    max={max ?? undefined}
                    step={entier ? '1' : 'any'}
                    placeholder={t('parametresAvances.sansBorne')}
                    data-testid={`parametre-${cle}`}
                    value={valeurs[cle]}
                    onChange={(e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }))}
                    className={CLASSE_CHAMP}
                  />
                  {suffixe && <span className="text-sm text-gray-500 whitespace-nowrap">{suffixe}</span>}
                </div>
                <span className="block text-xs text-gray-500 mt-1">
                  {t(`parametresAvances.aide.${cle}`)}
                </span>
              </label>
            ))}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              data-testid="parametres-enregistrer"
              disabled={envoi}
              className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <FaSave size={14} />
              {envoi ? t('parametresAvances.envoi') : t('parametresAvances.enregistrer')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ParametresAvances;
